import { z } from "zod";

/*
 * Runtime schemas of the API responses (spec 13.3). They mirror `backend/openapi.json`; the
 * generated types in `schema.gen.ts` are the compile-time reference. Schemas are not strict so
 * that additive server changes do not break older clients.
 */

/** ISO 8601 instant with offset, as returned by the API. */
export const instantSchema = z.iso.datetime({ offset: true });

/** ISO 8601 calendar date (`yyyy-MM-dd`). */
export const localDateSchema = z.iso.date();

/** Clock state of an employee. */
export const clockStateSchema = z.enum(["Off", "Working", "OnBreak"]);

/** Clock event kinds. */
export const clockEventKindSchema = z.enum(["ClockIn", "ClockOut", "BreakStart", "BreakEnd"]);

/** Declared kind of a break (ADR-0092). */
export const breakQualificationSchema = z.enum(["Unclassified", "RestBreak", "Standby"]);

/** Roles known to the client (spec 2.2); unknown roles are kept as plain strings. */
export const roleSchema = z.string();

/** `GET /time`. */
export const serverTimeSchema = z.object({
  serverTime: instantSchema,
});

/** An open working day of the clock state with the server's threshold (F-13, ADR-0128). */
export const openDaySchema = z.object({
  date: localDateSchema,
  /** Instant from which the day counts as incomplete (`OpenWorkDayPolicy` of the server). */
  incompleteFrom: instantSchema,
});

/** `GET /me/clock-state` and part of `GET /me`. */
export const clockStateResponseSchema = z.object({
  state: clockStateSchema,
  since: instantSchema.nullable(),
  openDays: z.array(localDateSchema),
  earliestRegularStart: instantSchema.nullable(),
  /**
   * The open days with their threshold (F-13); missing in snapshots stored by earlier app versions
   * and in answers of older servers.
   */
  openDayDetails: z.array(openDaySchema).optional(),
});

/** Kinds of work time models. */
export const workTimeModelKindSchema = z.enum(["Fixed", "Flexitime", "TrustBased", "Shift"]);

/** Employment types. */
export const employmentTypeSchema = z.enum(["Regular", "MarginalMiniJob", "Trainee", "Temporary"]);

/** Working time law of an employment (F-105): employees under the ArbZG or federal civil servants under the AZV. */
export const workingTimeLawSchema = z.enum(["ArbZG", "FederalCivilServantAzv"]);

/** Working time law of an employment. */
export type WorkingTimeLaw = z.infer<typeof workingTimeLawSchema>;

/** Active employment in `GET /me`. */
export const employmentSummarySchema = z.object({
  employmentId: z.uuid(),
  validFrom: localDateSchema,
  validTo: localDateSchema.nullable(),
  employmentType: employmentTypeSchema,
  establishmentId: z.uuid(),
  establishmentName: z.string().nullable(),
  timeZoneId: z.string().nullable(),
  workTimeModelName: z.string().nullable(),
  workTimeModelKind: workTimeModelKindSchema.nullable(),
  weeklyTargetSeconds: z.number().int().nullable(),
});

/** `GET /me`. */
/** Service state of the tenant (spec 20.3.3, T-113, T-137). */
export const serviceStatusSchema = z.enum(["Active", "PastDue", "Suspended", "ExportWindow"]);

/** Service state of the tenant. */
export type ServiceStatus = z.infer<typeof serviceStatusSchema>;

export const meSchema = z.object({
  employeeId: z.uuid(),
  tenantId: z.uuid(),
  personnelNumber: z.string(),
  displayName: z.string(),
  roles: z.array(roleSchema),
  employment: employmentSummarySchema.nullable(),
  clockState: clockStateResponseSchema,
  minorUntil: localDateSchema.nullable(),
  serviceStatus: serviceStatusSchema,
  exportWindowEndsAt: z.iso.datetime({ offset: true }).nullable(),
  /** Establishments the HR role is limited to (T-172); `null` or missing without a limit. */
  establishmentScope: z.array(z.uuid()).nullish(),
});

/** Server time response. */
export type ServerTime = z.infer<typeof serverTimeSchema>;
/** Clock state. */
export type ClockState = z.infer<typeof clockStateSchema>;
/** Clock event kind. */
export type ClockEventKind = z.infer<typeof clockEventKindSchema>;
/** Declared kind of a break. */
export type BreakQualification = z.infer<typeof breakQualificationSchema>;
/** Open working day of the clock state with the server's threshold. */
export type OpenDayResponse = z.infer<typeof openDaySchema>;
/** Clock state response. */
export type ClockStateResponse = z.infer<typeof clockStateResponseSchema>;
/** Active employment summary. */
export type EmploymentSummary = z.infer<typeof employmentSummarySchema>;
/** Current user profile. */
export type Me = z.infer<typeof meSchema>;

/** Severity of a compliance finding. */
export const findingSeveritySchema = z.enum(["Info", "Warning", "Violation", "IncompleteInput"]);

/** Unit of finding values. */
export const findingUnitSchema = z.enum(["Seconds", "Days", "Count"]);

/** Compliance finding of a day or period. */
export const findingSchema = z.object({
  ruleId: z.string(),
  check: z.string(),
  severity: findingSeveritySchema,
  legalReference: z.string(),
  from: localDateSchema,
  to: localDateSchema,
  actualValue: z.number(),
  limitValue: z.number(),
  unit: findingUnitSchema,
  profileId: z.string(),
  profileVersion: z.string(),
});

/** Work or break block. */
export const workBlockSchema = z.object({
  kind: z.enum(["Work", "Break"]),
  start: instantSchema,
  end: instantSchema,
  durationSeconds: z.number().int(),
  projectId: z.uuid().nullable(),
  isOpen: z.boolean(),
  /** Event that started the block (for corrections); `null` from servers before T-056. */
  startEventId: z.uuid().nullable().default(null),
  /** Event that ended the block; `null` while open. */
  endEventId: z.uuid().nullable().default(null),
  /** Declared kind of a break block; `null` for work blocks and from servers before T-150. */
  breakQualification: breakQualificationSchema.nullable().default(null),
});

/** Working day. */
export const workDaySchema = z.object({
  date: localDateSchema,
  timeZoneId: z.string(),
  isClosed: z.boolean(),
  workSeconds: z.number().int(),
  breakSeconds: z.number().int(),
  firstStart: instantSchema,
  lastEnd: instantSchema,
  blocks: z.array(workBlockSchema),
  findings: z.array(findingSchema),
});

/** `GET /me/workdays`. */
export const workDaysSchema = z.object({
  days: z.array(workDaySchema),
  periodFindings: z.array(findingSchema),
});

/** Sources of clock events. */
export const eventSourceSchema = z.enum([
  "Web",
  "Pwa",
  "Ios",
  "Android",
  "Terminal",
  "ManualEntry",
  "Import",
  "PolicyDeduction",
  "NfcTag",
]);

/** Error in an event result. */
export const errorResponseSchema = z.object({ code: z.string(), message: z.string() });

/** Result per submitted event. */
export const eventResultSchema = z.object({
  id: z.uuid(),
  status: z.enum(["Accepted", "Duplicate", "Rejected"]),
  error: errorResponseSchema.nullable(),
  hints: z.array(z.string()),
  suggestedAction: z.enum(["None", "CorrectionRequest"]),
});

/** `POST /events:batch`. */
export const eventBatchResponseSchema = z.object({
  results: z.array(eventResultSchema),
});

/** Compliance finding. */
export type Finding = z.infer<typeof findingSchema>;
/** Work block response. */
export type WorkBlockResponse = z.infer<typeof workBlockSchema>;
/** Working day response. */
export type WorkDayResponse = z.infer<typeof workDaySchema>;
/** Working days response. */
export type WorkDaysResponse = z.infer<typeof workDaysSchema>;
/** Event source. */
export type EventSource = z.infer<typeof eventSourceSchema>;
/** Result of a submitted event. */
export type EventResult = z.infer<typeof eventResultSchema>;
/** Batch response. */
export type EventBatchResponse = z.infer<typeof eventBatchResponseSchema>;

/** One event of `POST /events:batch` (request body). */
export interface EventRequestBody {
  readonly id: string;
  readonly employeeId: string | null;
  readonly kind: ClockEventKind;
  readonly occurredAt: string;
  readonly timeZoneId: string;
  readonly source: EventSource;
  readonly deviceId: string | null;
  readonly projectId: string | null;
  readonly costCenterId: string | null;
  /** Coarse location of an own event, rounded on the device (T-175); `null` otherwise. */
  readonly location: { readonly latitude: number; readonly longitude: number } | null;
  readonly clientClockOffsetMs: number | null;
  /** Declared kind of a break start; `null` for other events (ADR-0092). */
  readonly breakQualification: BreakQualification | null;
}

/** Balance summary of a period. */
export const balanceSummarySchema = z.object({
  from: localDateSchema,
  to: localDateSchema,
  targetSeconds: z.number().int(),
  actualSeconds: z.number().int(),
  absenceCreditSeconds: z.number().int(),
  overtimeSeconds: z.number().int(),
  flexitimeSeconds: z.number().int(),
  vacationDays: z.number(),
});

/** `GET /me/balances`. */
export const balancesSchema = z.object({
  asOf: localDateSchema,
  month: balanceSummarySchema,
  year: balanceSummarySchema,
  carryOver: balanceSummarySchema.nullable(),
  flexitimeCapStatus: z.enum(["WithinCaps", "AbovePositiveCap", "BelowNegativeCap"]),
  hasTimeAccount: z.boolean(),
});

/** Balance summary. */
export type BalanceSummary = z.infer<typeof balanceSummarySchema>;
/** Balances response. */
export type Balances = z.infer<typeof balancesSchema>;

/** Kinds of correction changes. */
export const correctionChangeKindSchema = z.enum(["AddEvent", "ReplaceEvent", "VoidEvent"]);

/** Status of a correction request. */
export const correctionStatusSchema = z.enum([
  "Pending",
  "Approved",
  "Rejected",
  "AutoApproved",
  /** Withdrawn by the person who asked before a decision (F-61). */
  "Withdrawn",
]);

/** One change of a correction request. */
export const correctionChangeSchema = z.object({
  kind: correctionChangeKindSchema,
  eventKind: clockEventKindSchema.nullable(),
  occurredAt: instantSchema.nullable(),
  timeZoneId: z.string().nullable(),
  targetEventId: z.uuid().nullable(),
  projectId: z.uuid().nullable(),
  /** Declared kind of a new break start; `null` keeps that of a replaced one (ADR-0092). */
  breakQualification: breakQualificationSchema.nullable().default(null),
});

/** Correction request. */
export const correctionSchema = z.object({
  id: z.uuid(),
  employeeId: z.uuid(),
  requestedByEmployeeId: z.uuid(),
  requestedAt: instantSchema,
  reason: z.string(),
  status: correctionStatusSchema,
  decidedByEmployeeId: z.uuid().nullable(),
  decidedAt: instantSchema.nullable(),
  decisionNote: z.string().nullable(),
  changes: z.array(correctionChangeSchema),
  /** The request changed a month closed for the person when it was made (T-171); missing before T-171: no. */
  touchesLockedMonth: z.boolean().default(false),
});

/** `GET /corrections`. */
export const correctionListSchema = z.array(correctionSchema);

/** `POST /corrections:preview`. */
export const correctionPreviewSchema = z.object({
  before: workDaysSchema,
  after: workDaysSchema,
  /** The correction changes a month closed for the person (T-171); missing before T-171: no. */
  touchesLockedMonth: z.boolean().default(false),
});

/** Correction change. */
export type CorrectionChange = z.infer<typeof correctionChangeSchema>;
/** Correction request. */
export type Correction = z.infer<typeof correctionSchema>;
/** Correction status. */
export type CorrectionStatus = z.infer<typeof correctionStatusSchema>;
/** Correction preview. */
export type CorrectionPreview = z.infer<typeof correctionPreviewSchema>;

/** Body of `POST /corrections` and `POST /corrections:preview`. */
export interface CorrectionRequestBody {
  readonly employeeId: string | null;
  readonly reason: string;
  readonly changes: readonly CorrectionChange[];
}

/**
 * Kinds of absences. A kind a newer server knows and this client does not is read as `Other`
 * („Sonstige Abwesenheit“) instead of failing the whole list, so an older client keeps working after a new
 * kind is introduced (F-21 rollout, ADR-0133); the server never sends `Other`.
 */
export const absenceKindSchema = z
  .enum([
    "Vacation",
    "Sick",
    "Holiday",
    "Special",
    "Training",
    "CompensatoryRest",
    "Rehabilitation",
    "EducationLeave",
    "VocationalTraining",
    "Other",
  ])
  .catch("Other");

/**
 * Category of a release for vocational school or an examination. An unknown category of a newer server reads as
 * `Other` („Berufsschule oder Prüfung“) instead of failing the whole list, like {@link absenceKindSchema}; the server
 * never sends `Other`, and a request never carries it.
 */
export const vocationalTrainingCategorySchema = z
  .enum(["SchoolDay", "BlockWeek", "Lessons", "Examination", "ExamEve", "Other"])
  .catch("Other");

/**
 * A release for vocational school or an examination (§ 15 BBiG, §§ 9, 10 JArbSchG; F-52, ADR-0173): category and, for
 * lessons and examinations, the local times and the travel time, for a block week the daily start of the lessons if
 * given. The server sends times as `HH:mm:ss`.
 */
export const vocationalTrainingSchema = z.object({
  category: vocationalTrainingCategorySchema,
  startsAt: z
    .string()
    .regex(/^\d{2}:\d{2}(:\d{2})?$/u)
    .nullish(),
  endsAt: z
    .string()
    .regex(/^\d{2}:\d{2}(:\d{2})?$/u)
    .nullish(),
  travelMinutes: z.number().int().optional(),
});

/** A release for vocational school or an examination. */
export type VocationalTraining = z.infer<typeof vocationalTrainingSchema>;

/** Status of an absence. */
export const absenceStatusSchema = z.enum(["Requested", "Approved", "Rejected", "Cancelled"]);

/**
 * Whether the leave account covers an open vacation request (T-163, `LINV-07`): requests it does
 * not cover, or before the start of the person's account, are decided by HR with a reason.
 */
export const leaveCoverageSchema = z.enum(["Covered", "NotCovered", "BeforeAccountStart"]);

/** An absence. */
export const absenceSchema = z.object({
  id: z.uuid(),
  employeeId: z.uuid(),
  kind: absenceKindSchema,
  from: localDateSchema,
  to: localDateSchema,
  isHalfDay: z.boolean(),
  status: absenceStatusSchema,
  /** Reason HR gave for approving vacation above the leave account (T-163). */
  approvalReason: z.string().nullish(),
  /** In lists, for an open vacation request with a leave account: whether the account covers it. */
  leaveCoverage: leaveCoverageSchema.nullish(),
  /** In lists: whether HR approves the open request only with a reason (not covered or unknown). */
  approvalNeedsReason: z.boolean().optional(),
  /** Reason of the last rejection of the request or of its cancellation (T-167). */
  rejectionReason: z.string().nullish(),
  /** Reason of an HR administrator who cancelled the absence (T-167). */
  cancellationReason: z.string().nullish(),
  /** Colleague named as substitute (T-167). */
  substituteEmployeeId: z.uuid().nullish(),
  /** Company shutdown that booked the vacation (T-167). */
  companyShutdownId: z.uuid().nullish(),
  /** Whether the cancellation of the approved absence is requested (T-167). */
  cancellationRequested: z.boolean().optional(),
  /** Category, times and travel of a release for vocational school or an examination (F-52). */
  vocationalTraining: vocationalTrainingSchema.nullish(),
});

/** `GET /absences`. */
export const absenceListSchema = z.array(absenceSchema);

/** Absence kind. */
export type AbsenceKind = z.infer<typeof absenceKindSchema>;
/** Absence status. */
export type AbsenceStatus = z.infer<typeof absenceStatusSchema>;
/** Absence. */
export type Absence = z.infer<typeof absenceSchema>;
/** Whether the leave account covers an open vacation request. */
export type LeaveCoverage = z.infer<typeof leaveCoverageSchema>;

/** Mode of a leave blackout (T-167): only a warning, or own requests are refused. */
export const leaveBlackoutModeSchema = z.enum(["Warn", "Block"]);

/**
 * Planning hints of an open vacation request for the deciding person (T-167): leave blackouts,
 * overlaps in the team, the minimum staffing and the hint on § 7 Abs. 2 BUrlG.
 */
export const absencePlanningHintsSchema = z.object({
  blackout: leaveBlackoutModeSchema.nullish(),
  overlappingAbsences: z.number().int().nullish(),
  staffingShortfallOn: localDateSchema.nullish(),
  withoutConsecutiveWorkdayBlock: z.boolean().nullish(),
});

/** Planning hints of an open vacation request. */
export type AbsencePlanningHints = z.infer<typeof absencePlanningHintsSchema>;

/**
 * An absence in the overview of managers and HR administrators (ADR-0110). Without the right to
 * read health data the kind of a sickness is missing; the absence is shown as such only.
 */
export const absenceOverviewItemSchema = z.object({
  id: z.uuid(),
  employeeId: z.uuid(),
  kind: absenceKindSchema.nullish(),
  from: localDateSchema,
  to: localDateSchema,
  isHalfDay: z.boolean(),
  status: absenceStatusSchema,
  canDecide: z.boolean(),
  /** Only whether the leave account covers an open vacation request, no numbers (spec 6.6.4). */
  leaveCoverage: leaveCoverageSchema.nullish(),
  /** Whether the caller may approve it; missing from older servers, then as `canDecide`. */
  canApprove: z.boolean().optional(),
  /** Whether the caller approves it only with a reason (not covered, or coverage unknown). */
  approvalNeedsReason: z.boolean().optional(),
  /** Whether the person asks to cancel the approved absence; approve and reject decide it (T-167). */
  cancellationRequested: z.boolean().optional(),
  /** Whether the open request to cancel concerns absence that has begun: it can only be rejected (T-167). */
  cancellationAfterStart: z.boolean().optional(),
  /** Colleague named as substitute (T-167). */
  substituteEmployeeId: z.uuid().nullish(),
  /** Whether a company shutdown booked the vacation (T-167). */
  companyShutdown: z.boolean().optional(),
  /** Planning hints of an open vacation request the caller may decide (T-167). */
  hints: absencePlanningHintsSchema.nullish(),
  /** Category, times and travel of a release for vocational school the caller may decide (F-52). */
  vocationalTraining: vocationalTrainingSchema.nullish(),
});

/** `GET /absences/overview`. */
export const absenceOverviewSchema = z.array(absenceOverviewItemSchema);

/** Absence in the overview of managers and HR administrators. */
export type AbsenceOverviewItem = z.infer<typeof absenceOverviewItemSchema>;

/** Body of `POST /absences`. */
export interface AbsenceRequestBody {
  readonly employeeId: string | null;
  readonly kind: AbsenceKind;
  readonly from: string;
  readonly to: string;
  readonly isHalfDay: boolean;
  /** Reason of HR for recording vacation above the leave account (T-163). */
  readonly reason?: string;
  /** Colleague named as substitute (T-167). */
  readonly substituteEmployeeId?: string;
  /** Category, times (`HH:mm`) and travel of a release for vocational school or an examination (F-52). */
  readonly vocationalTraining?: {
    readonly category: Exclude<VocationalTraining["category"], "Other">;
    readonly startsAt?: string;
    readonly endsAt?: string;
    readonly travelMinutes?: number;
  };
}

/** Finding in the team overview (rule and severity only). */
export const teamFindingSchema = z.object({
  ruleId: z.string(),
  severity: findingSeveritySchema,
});

/** Team member on a date. */
export const teamMemberSchema = z.object({
  employeeId: z.uuid(),
  displayName: z.string(),
  personnelNumber: z.string(),
  clockState: clockStateResponseSchema,
  workSeconds: z.number().int(),
  breakSeconds: z.number().int(),
  firstStart: instantSchema.nullable(),
  isClosed: z.boolean().nullable(),
  findings: z.array(teamFindingSchema),
  /** Steps of the date verified offline at a terminal (T-173). */
  offlineTerminalSteps: z.number().int().default(0),
  /**
   * The running shift while clocked in, also one that began before the date, with its working time so far (F-104);
   * `null` while clocked out, if the member was not in the team on the shift's day, or from an older server.
   */
  runningShift: z
    .object({ date: localDateSchema, workSeconds: z.number().int() })
    .nullable()
    .default(null),
  /**
   * Whether the caller sees the member on the date only within § 7 (8) AZV (F-116): then the clock state has no
   * instants and times, findings and the running shift are left out; `false` from an older server.
   */
  azvLimited: z.boolean().default(false),
});

/** `GET /teams/{managerId}/workdays`. */
export const teamSchema = z.object({
  date: localDateSchema,
  members: z.array(teamMemberSchema),
});

/** Above the positive or below the negative threshold of § 7 (8) sentence 2 AZV (F-116). */
export const azvBalanceNoticeKindSchema = z.enum([
  "AbovePositiveThreshold",
  "BelowNegativeThreshold",
]);

/** Kind of a notice about a flexitime balance. */
export type AzvBalanceNoticeKind = z.infer<typeof azvBalanceNoticeKindSchema>;

/** `GET /teams/{managerId}/azv-balance-notices` (F-116): only whether a threshold is crossed, never the balance. */
export const azvBalanceNoticesSchema = z.object({
  date: localDateSchema,
  positiveThresholdHours: z.number().int(),
  negativeThresholdHours: z.number().int(),
  notices: z.array(
    z.object({
      employeeId: z.uuid(),
      displayName: z.string(),
      personnelNumber: z.string(),
      notice: azvBalanceNoticeKindSchema,
    }),
  ),
});

/** Notices about the flexitime balances of federal civil servants for their immediate superior. */
export type AzvBalanceNotices = z.infer<typeof azvBalanceNoticesSchema>;

/** Status of a stored violation. */
export const violationStatusSchema = z.enum(["Open", "Acknowledged", "Resolved"]);

/** Acknowledgement of a violation. */
export const acknowledgementSchema = z.object({
  employeeId: z.uuid(),
  at: instantSchema,
  comment: z.string(),
  /** The detection it acknowledged (F-112); 1 from a server before F-112. */
  detection: z.number().int().positive().default(1),
});

/** Stored compliance violation (health-related rules masked as `PROTECTION-RULE`). */
export const violationSchema = z.object({
  id: z.uuid(),
  employeeId: z.uuid(),
  ruleId: z.string(),
  check: z.string().nullable(),
  severity: findingSeveritySchema,
  legalReference: z.string().nullable(),
  from: localDateSchema,
  to: localDateSchema,
  actualValue: z.number().nullable(),
  limitValue: z.number().nullable(),
  unit: findingUnitSchema.nullable(),
  profileId: z.string().nullable(),
  profileVersion: z.string().nullable(),
  status: violationStatusSchema,
  detectedAt: instantSchema,
  resolvedAt: instantSchema.nullable(),
  acknowledgements: z.array(acknowledgementSchema),
  /**
   * Number of the current detection (F-112): one more every time the stored finding became a
   * violation; 1 from a server before F-112.
   */
  detection: z.number().int().positive().default(1),
});

/** `GET /violations`. */
export const violationListSchema = z.array(violationSchema);

/** Employee (master data). */
export const employeeSchema = z.object({
  id: z.uuid(),
  personnelNumber: z.string(),
  displayName: z.string(),
  externalSubject: z.string(),
  minorUntil: localDateSchema.nullable(),
});

/** `GET /employees`. */
export const employeeListSchema = z.array(employeeSchema);

/** Team member. */
export type TeamMember = z.infer<typeof teamMemberSchema>;
/** Team overview. */
export type Team = z.infer<typeof teamSchema>;
/** Violation status. */
export type ViolationStatus = z.infer<typeof violationStatusSchema>;
/** Violation. */
export type Violation = z.infer<typeof violationSchema>;
/** Employee. */
export type Employee = z.infer<typeof employeeSchema>;

/** German federal states. */
export const federalStateSchema = z.enum([
  "BW",
  "BY",
  "BE",
  "BB",
  "HB",
  "HH",
  "HE",
  "MV",
  "NI",
  "NW",
  "RP",
  "SL",
  "SN",
  "ST",
  "SH",
  "TH",
]);

/** Establishment. `industries` is a comma-separated flag list such as `Bakery, Hospital` or `None`. */
export const establishmentSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  timeZoneId: z.string(),
  federalState: federalStateSchema,
  industries: z.string(),
  sundayWorkExemption: z.string().nullable(),
  regionalHolidays: z.array(z.string()),
});

/** The figures of one establishment (`GET /establishments/overview`, T-172). */
export const establishmentFiguresSchema = z.object({
  establishmentId: z.uuid(),
  name: z.string(),
  activeEmployees: z.number().int().nonnegative(),
  openDays: z.number().int().nonnegative(),
  openViolations: z.number().int().nonnegative(),
});

/** The overview per establishment of today (T-172). */
export const establishmentOverviewSchema = z.object({
  date: z.string(),
  establishments: z.array(establishmentFiguresSchema),
});

/** Days of the week as the API writes them. */
export const dayOfWeekSchema = z.enum([
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
]);

/** Work time model. */
export const workTimeModelSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: workTimeModelKindSchema,
  weeklyTargetSeconds: z.number().int(),
  workingDays: z.array(dayOfWeekSchema),
  coreTimeStart: z.string().nullable(),
  coreTimeEnd: z.string().nullable(),
  flexitimeMaxPositiveSeconds: z.number().int().nullable(),
  flexitimeMaxNegativeSeconds: z.number().int().nullable(),
  /** History of the option „Soll aus dem Dienstplan“ (shift models, T-180) in date order. */
  rosterTargetChanges: z
    .array(z.object({ from: localDateSchema, enabled: z.boolean() }))
    .default([]),
});

/** Rule profile assignment of an employment. */
export const ruleProfileAssignmentSchema = z.object({
  profileId: z.string(),
  validFrom: localDateSchema,
  validTo: localDateSchema.nullable(),
});

/** Facts of an employment for statutory recording obligations (T-152) and § 21a ArbZG (T-155). */
export const employmentRecordingScopeSchema = z.object({
  isHiredInAgencyWorker: z.boolean(),
  agencyWageOrdinanceApplies: z.boolean(),
  confirmedApplicationRules: z.array(z.object({ ruleId: z.string(), version: z.string() })),
  /** Driver or co-driver in road transport (§ 21a ArbZG, T-155). */
  isRoadTransportDriver: z.boolean().default(false),
  /** HR confirms an exception of § 24 (2) JArbSchG for underground work of a minor (F-88). */
  isUndergroundYouthExceptionConfirmed: z.boolean().default(false),
});

/** Employment (master data). */
export const employmentDetailSchema = z.object({
  id: z.uuid(),
  employeeId: z.uuid(),
  establishmentId: z.uuid(),
  validFrom: localDateSchema,
  validTo: localDateSchema.nullable(),
  employmentType: employmentTypeSchema,
  workTimeModelId: z.uuid(),
  isExemptFromArbZG: z.boolean(),
  worksRotatingShifts: z.boolean(),
  managerEmployeeId: z.uuid().nullable(),
  ruleProfileAssignments: z.array(ruleProfileAssignmentSchema),
  /** Team of the employment (T-120). */
  teamId: z.uuid().nullable().default(null),
  /** Facts for statutory recording obligations (T-152). */
  recordingScope: employmentRecordingScopeSchema.default({
    isHiredInAgencyWorker: false,
    agencyWageOrdinanceApplies: false,
    confirmedApplicationRules: [],
    isRoadTransportDriver: false,
    isUndergroundYouthExceptionConfirmed: false,
  }),
  /** Working time law (F-105); missing from servers before F-105: the ArbZG. */
  workingTimeLaw: workingTimeLawSchema.default("ArbZG"),
});

/** Rule profile of the catalog. */
export const ruleProfileSchema = z.object({
  id: z.string(),
  version: z.string(),
  kind: z.string(),
  validFrom: localDateSchema,
  validTo: localDateSchema.nullable(),
  description: z.string(),
  /** Working time law of a base profile (F-115); missing from servers before F-115: the ArbZG. */
  workingTimeLaw: workingTimeLawSchema.default("ArbZG"),
});

/** Versioned application rule of a recording obligation (MiLoDokV, MiLoAufzV; T-152). */
export const recordingApplicationRuleSchema = z.object({
  id: z.string(),
  version: z.string(),
  kind: z.enum(["Exemption", "Simplification"]),
  legalReference: z.string(),
  validFrom: localDateSchema,
  validTo: localDateSchema.nullable(),
  description: z.string(),
  prerequisites: z.array(z.string()),
  recordingMode: z.enum(["StartEndDuration", "DurationOnly"]).nullable(),
  isConfigured: z.boolean(),
});

/** Timeliness requirements of a recording obligation (§ 6 Abs. 1 GSA Fleisch, T-153). */
export const recordingTimelinessSchema = z.object({
  startToleranceSeconds: z.number().int().nonnegative(),
  endOnDayOfWork: z.boolean(),
});

/** Statutory recording obligation profile (`GET /recording-obligations`, T-152). */
export const recordingObligationSchema = z.object({
  id: z.string(),
  version: z.string(),
  ruleId: z.string(),
  legalReference: z.string(),
  validFrom: localDateSchema,
  validTo: localDateSchema.nullable(),
  description: z.string(),
  requiredFields: z.array(z.string()),
  recordingDeadlineDays: z.number().int().nullable(),
  retentionYears: z.number().int().nullable(),
  applicationRules: z.array(recordingApplicationRuleSchema),
  electronicTamperProof: z.boolean(),
  timeliness: recordingTimelinessSchema.nullable(),
});

/** Opt-out consent (§ 7 Abs. 7 ArbZG). */
export const optOutConsentSchema = z.object({
  id: z.uuid(),
  grantedAt: instantSchema,
  revokedAt: instantSchema.nullable(),
  documentReference: z.string(),
});

/** Maternity protection period (health data). */
export const protectionPeriodSchema = z.object({
  id: z.uuid().nullable(),
  validFrom: localDateSchema,
  validTo: localDateSchema.nullable(),
  hasSundayWorkConsent: z.boolean(),
  hasEveningWorkPermit: z.boolean(),
});

/** Period of another employment of the employee (T-151); the other employer is not recorded. */
export const multipleEmploymentPeriodSchema = z.object({
  id: z.uuid(),
  validFrom: localDateSchema,
  validTo: localDateSchema.nullable(),
  recordedAt: instantSchema,
});

/** A stretch of work at other employers (F-50). */
export const externalWorkIntervalSchema = z.object({
  start: instantSchema,
  end: instantSchema,
});

/**
 * Working and shift time of one day at all other employers together (T-151) and, if known, the
 * intervals of that work (F-50).
 */
export const externalWorkDaySchema = z.object({
  date: localDateSchema,
  workSeconds: z.number().int().nonnegative(),
  shiftSeconds: z.number().int().nonnegative().nullable(),
  /** Missing on servers before F-50. */
  intervals: z.array(externalWorkIntervalSchema).nullable().default(null),
});

/** Kind of source of a declaration, never the other employer (F-50). */
export const externalWorkingTimeSourceSchema = z.enum(["SelfDeclaration", "OtherEmployerRecord"]);

/** Declaration of the working time at other employers for a period (T-151). */
export const externalWorkingTimeDeclarationSchema = z.object({
  id: z.uuid(),
  from: localDateSchema,
  to: localDateSchema,
  days: z.array(externalWorkDaySchema),
  /** Missing on servers before F-50. */
  source: externalWorkingTimeSourceSchema.default("SelfDeclaration"),
  declaredAt: instantSchema,
  withdrawnAt: instantSchema.nullable(),
});

/** Phase of a preparation or follow-up time (T-153). */
export const preparationPhaseSchema = z.enum(["Preparation", "FollowUp"]);

/** Activity serving the employer during a preparation or follow-up time (T-153). */
export const preparationActivitySchema = z.enum([
  "ChangingClothes",
  "Washing",
  "SetUp",
  "InternalWalking",
  "Other",
]);

/** Part of the recorded working time categorized as preparation or follow-up (T-153). */
export const preparationTimeSchema = z.object({
  id: z.uuid(),
  workDate: localDateSchema,
  phase: preparationPhaseSchema,
  activity: preparationActivitySchema,
  start: instantSchema,
  end: instantSchema,
  durationSeconds: z.number().int().positive(),
  recordedAt: instantSchema,
  withdrawnAt: instantSchema.nullable(),
});

/** Establishment. */
export type Establishment = z.infer<typeof establishmentSchema>;
/** The figures of one establishment. */
export type EstablishmentFigures = z.infer<typeof establishmentFiguresSchema>;
/** The overview per establishment of today. */
export type EstablishmentOverview = z.infer<typeof establishmentOverviewSchema>;
/** Federal state. */
export type FederalState = z.infer<typeof federalStateSchema>;
/** Day of week. */
export type DayOfWeek = z.infer<typeof dayOfWeekSchema>;
/** Work time model. */
export type WorkTimeModel = z.infer<typeof workTimeModelSchema>;
/** Employment. */
export type EmploymentDetail = z.infer<typeof employmentDetailSchema>;
/** Rule profile. */
export type RuleProfile = z.infer<typeof ruleProfileSchema>;
/** Statutory recording obligation profile. */
export type RecordingObligation = z.infer<typeof recordingObligationSchema>;
/** Opt-out consent. */
export type OptOutConsent = z.infer<typeof optOutConsentSchema>;
/** Protection period. */
export type ProtectionPeriod = z.infer<typeof protectionPeriodSchema>;
/** Period of another employment. */
export type MultipleEmploymentPeriod = z.infer<typeof multipleEmploymentPeriodSchema>;
/** Declared day at other employers. */
export type ExternalWorkDay = z.infer<typeof externalWorkDaySchema>;
/** Kind of source of a declaration of external working time. */
export type ExternalWorkingTimeSource = z.infer<typeof externalWorkingTimeSourceSchema>;
/** Declaration of external working time. */
export type ExternalWorkingTimeDeclaration = z.infer<typeof externalWorkingTimeDeclarationSchema>;
/** Phase of a preparation or follow-up time. */
export type PreparationPhase = z.infer<typeof preparationPhaseSchema>;
/** Activity of a preparation or follow-up time. */
export type PreparationActivity = z.infer<typeof preparationActivitySchema>;
/** Preparation or follow-up time categorized as working time. */
export type PreparationTime = z.infer<typeof preparationTimeSchema>;

/** Public holiday of the caller's establishment. */
export const holidaySchema = z.object({
  date: localDateSchema,
  name: z.string(),
  isRegional: z.boolean(),
});

/** `GET /me/holidays`. */
export const holidayListSchema = z.array(holidaySchema);

/** Public holiday. */
export type Holiday = z.infer<typeof holidaySchema>;

/** `GET /privacy-information`: controller and data protection contact (Art. 13 GDPR). */
export const privacyInformationSchema = z.object({
  employerName: z.string(),
  dataProtectionContact: z.string().nullable(),
});

/** Privacy information. */
export type PrivacyInformation = z.infer<typeof privacyInformationSchema>;

/** Roles of a tenant (T-104); the names of the backend enumeration. */
export const tenantRoleSchema = z.enum([
  "Employee",
  "Manager",
  "HrAdmin",
  "TenantAdmin",
  "Auditor",
  "WorksCouncil",
  "BillingAdmin",
]);

/** A tenant role. */
export type TenantRole = z.infer<typeof tenantRoleSchema>;

/** An employee with the roles valid on the requested date (`GET /memberships`). */
export const employeeRolesSchema = z.object({
  employeeId: z.string(),
  displayName: z.string(),
  personnelNumber: z.string(),
  roles: z.array(tenantRoleSchema),
  validFrom: z.string().nullable(),
  validTo: z.string().nullable(),
  /** Establishments the HR role is limited to (T-172); `null` for the whole tenant or an unreadable limit. */
  establishmentIds: z.array(z.string()).nullish(),
  /** Whether the stored limit cannot be read (F-36): the HR role sees no establishment. */
  establishmentScopeUnreadable: z.boolean().default(false),
});

/** Employees with their roles. */
export const employeeRolesListSchema = z.array(employeeRolesSchema);

/** An employee with roles. */
export type EmployeeRoles = z.infer<typeof employeeRolesSchema>;

/** A membership (roles for a period). */
export const membershipSchema = z.object({
  id: z.string(),
  employeeId: z.string(),
  roles: z.array(tenantRoleSchema),
  validFrom: z.string(),
  validTo: z.string().nullable(),
  /** Establishments the HR role is limited to (T-172); `null` for the whole tenant or an unreadable limit. */
  establishmentIds: z.array(z.string()).nullish(),
  /** Whether the stored limit cannot be read (F-36): the HR role sees no establishment. */
  establishmentScopeUnreadable: z.boolean().default(false),
});

/** Memberships of an employee. */
export const membershipListSchema = z.array(membershipSchema);

/** The employers of the signed-in person (`GET /me/tenants`, T-105). */
export const myTenantsSchema = z.array(z.object({ tenantId: z.string(), name: z.string() }));

/** A SCIM credential of the tenant (T-107). */
export const scimCredentialSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
});

/** A SCIM credential. */
export type ScimCredential = z.infer<typeof scimCredentialSchema>;

/** SCIM credentials, newest first. */
export const scimCredentialListSchema = z.array(scimCredentialSchema);

/** A new SCIM credential with its secret, which is shown once. */
export const issuedScimCredentialSchema = z.object({
  credential: scimCredentialSchema,
  secret: z.string(),
  scimBaseUrl: z.string(),
  tokenUrl: z.string(),
});

/** A new SCIM credential. */
export type IssuedScimCredential = z.infer<typeof issuedScimCredentialSchema>;

/** A client of the public API (T-140); the secret is never listed. */
export const apiClientSchema = z.object({
  id: z.string(),
  name: z.string(),
  scope: z.string(),
  expiresAt: z.string(),
  revoked: z.boolean(),
});

/** A client of the public API. */
export type ApiClient = z.infer<typeof apiClientSchema>;

/** Clients of the public API. */
export const apiClientListSchema = z.array(apiClientSchema);

/** A new client of the public API with its secret, which is shown once. */
export const issuedApiClientSchema = z.object({ client: apiClientSchema, secret: z.string() });

/** A new client of the public API. */
export type IssuedApiClient = z.infer<typeof issuedApiClientSchema>;

/** A webhook of the public API (T-140); the signing key is never listed. */
export const apiWebhookSchema = z.object({
  id: z.string(),
  url: z.string(),
  events: z.string(),
  enabled: z.boolean(),
});

/** A webhook of the public API. */
export type ApiWebhook = z.infer<typeof apiWebhookSchema>;

/** Webhooks of the public API. */
export const apiWebhookListSchema = z.array(apiWebhookSchema);

/** A new webhook with its signing key, which is shown once. */
export const issuedApiWebhookSchema = z.object({ webhook: apiWebhookSchema, secret: z.string() });

/** A new webhook of the public API. */
export type IssuedApiWebhook = z.infer<typeof issuedApiWebhookSchema>;

/** Payroll program that imports the payroll file (T-141); `Generic` is another program with the CSV of T-183. */
export const datevProgramSchema = z.enum(["Lodas", "LohnUndGehalt", "Generic"]);

/** DATEV payroll program. */
export type DatevProgram = z.infer<typeof datevProgramSchema>;

/** Monthly value of an employee that can be transferred to DATEV with a wage type (T-141). */
export const payrollCategorySchema = z.enum([
  "WorkedHours",
  "OvertimeHours",
  "AbsenceHours",
  "VacationDays",
  "SickDays",
  "LeavePayoutDays",
  "LeaveTakenDays",
  "OvertimePayoutHours",
  "FlexitimePayoutHours",
  "VacationHours",
  "SickHours",
  "SpecialLeaveHours",
  "TrainingHours",
  "EducationLeaveHours",
]);

/** Category of a DATEV wage type. */
export type PayrollCategory = z.infer<typeof payrollCategorySchema>;

/** Wage type of a category in the DATEV settings. */
export const datevWageTypeSchema = z.object({
  category: payrollCategorySchema,
  wageType: z.number().int(),
  processingKey: z.number().int(),
});

/** Wage type of a category. */
export type DatevWageType = z.infer<typeof datevWageTypeSchema>;

/** Wage type of a surcharge kind, by its stable code (T-183). */
export const datevSurchargeWageTypeSchema = z.object({
  code: z.string(),
  wageType: z.number().int(),
  processingKey: z.number().int(),
});

/** Wage type of a surcharge kind. */
export type DatevSurchargeWageType = z.infer<typeof datevSurchargeWageTypeSchema>;

/** Absence kinds that credit hours and can be transferred as calendar records (T-183). */
export const creditedAbsenceKindSchema = z.enum([
  "Vacation",
  "Sick",
  "Holiday",
  "Special",
  "Training",
  "Rehabilitation",
  "EducationLeave",
  "VocationalTraining",
]);

/** An absence kind that credits hours. */
export type CreditedAbsenceKind = z.infer<typeof creditedAbsenceKindSchema>;

/** Outage key („Ausfallschlüssel“) of an absence kind for the calendar records (T-183). */
export const datevAbsenceCalendarKeySchema = z.object({
  kind: creditedAbsenceKindSchema,
  outageKey: z.string(),
});

/** Outage key of an absence kind. */
export type DatevAbsenceCalendarKey = z.infer<typeof datevAbsenceCalendarKeySchema>;

/** Cost center of an establishment (T-183). */
export const datevCostCenterSchema = z.object({
  establishmentId: z.string(),
  costCenter: z.string(),
});

/** Cost center of an establishment. */
export type DatevCostCenter = z.infer<typeof datevCostCenterSchema>;

/** Column of the generic payroll export (T-183). */
export const payrollColumnSchema = z.enum([
  "PersonnelNumber",
  "Period",
  "WageType",
  "Quantity",
  "Unit",
  "CostCenter",
  "Date",
  "OutageKey",
  "Year",
  "Month",
]);

/** Column of the generic payroll export. */
export type PayrollColumn = z.infer<typeof payrollColumnSchema>;

/** How the overtime account reaches the payroll in a month (T-183, ADR-0155 #1). */
export const overtimeTransferSchema = z.enum(["Increase", "Payouts"]);

/** How the overtime account reaches the payroll. */
export type OvertimeTransfer = z.infer<typeof overtimeTransferSchema>;

/** A switch of the overtime mode: from the first day of `from` on, the overtime account is transferred as `transfer`. */
export const datevOvertimeSwitchSchema = z.object({
  from: z.string(),
  transfer: overtimeTransferSchema,
});

/** A switch of the overtime mode. */
export type DatevOvertimeSwitch = z.infer<typeof datevOvertimeSwitchSchema>;

/** Stored DATEV settings of the tenant. */
export const datevConfigurationSchema = z.object({
  program: datevProgramSchema,
  consultantNumber: z.number().int(),
  clientNumber: z.number().int(),
  wageTypes: z.array(datevWageTypeSchema),
  surchargeWageTypes: z.array(datevSurchargeWageTypeSchema).default([]),
  absenceCalendarKeys: z.array(datevAbsenceCalendarKeySchema).default([]),
  costCenters: z.array(datevCostCenterSchema).default([]),
  genericColumns: z.array(payrollColumnSchema).default([]),
  overtimeSwitches: z.array(datevOvertimeSwitchSchema).default([]),
});

/** Stored DATEV settings. */
export type DatevConfiguration = z.infer<typeof datevConfigurationSchema>;

/** `GET` and `PUT /exports/payroll/datev/settings` (T-141). */
export const datevSettingsSchema = z.object({
  isEntitled: z.boolean(),
  configuration: datevConfigurationSchema.nullable(),
});

/** DATEV settings and the entitlement of the plan. */
export type DatevSettings = z.infer<typeof datevSettingsSchema>;

/** What a value of the check log counts: a category, a surcharge code or the calendar of an absence kind (T-183). */
export const payrollValueKeySchema = z.object({
  category: payrollCategorySchema.nullable(),
  surchargeCode: z.string().nullable(),
  calendarKind: creditedAbsenceKindSchema.nullable(),
  inDays: z.boolean(),
});

/** What a value of the check log counts. */
export type PayrollValueKey = z.infer<typeof payrollValueKeySchema>;

/** A person named in the check log. */
export const payrollCheckPersonSchema = z.object({
  employeeId: z.string(),
  personnelNumber: z.string(),
  displayName: z.string().nullable(),
});

/** A person named in the check log. */
export type PayrollCheckPerson = z.infer<typeof payrollCheckPersonSchema>;

/** Hours paid out from a time account of a person in the month (check log, T-183). */
export const payrollCheckPayoutSchema = z.object({
  person: payrollCheckPersonSchema,
  account: z.enum(["Overtime", "Flexitime"]),
  hours: z.number(),
  openIncreaseHours: z.number().nullable().default(null),
});

/** Hours paid out from a time account. */
export type PayrollCheckPayout = z.infer<typeof payrollCheckPayoutSchema>;

/** `GET /exports/payroll/datev/check` (T-183): the check log of the payroll file of a month. */
export const payrollCheckLogSchema = z.object({
  month: z.string(),
  isProvisional: z.boolean(),
  totals: z.array(
    z.object({
      key: payrollValueKeySchema,
      wageType: z.number().int().nullable(),
      quantity: z.number(),
      records: z.number().int(),
    }),
  ),
  personsWithoutValues: z.array(payrollCheckPersonSchema),
  surchargeFailures: z.array(payrollCheckPersonSchema),
  unmappedSurchargeCodes: z.array(z.string()),
  previous: z
    .object({
      createdAt: z.string(),
      isProvisional: z.boolean(),
      program: datevProgramSchema,
      comparedWithPrevious: z.boolean().default(false),
      deviationCount: z.number().int().nullable().default(null),
    })
    .nullable(),
  deviations: z.array(
    z.object({
      person: payrollCheckPersonSchema,
      key: payrollValueKeySchema,
      wageType: z.number().int().nullable().default(null),
      date: z.string().nullable().default(null),
      previous: z.number(),
      current: z.number(),
    }),
  ),
  untransferredPayouts: z.array(payrollCheckPayoutSchema).default([]),
  possiblyTransferredPayouts: z.array(payrollCheckPayoutSchema).default([]),
  untransferredAbsences: z
    .array(
      z.object({ kind: creditedAbsenceKindSchema, hours: z.number(), persons: z.number().int() }),
    )
    .default([]),
  establishmentChanges: z.array(payrollCheckPersonSchema).default([]),
});

/** The check log of the payroll file of a month. */
export type PayrollCheckLog = z.infer<typeof payrollCheckLogSchema>;

/** An emergency access of operator support as the tenant administrator sees it (T-111). */
export const emergencyAccessSchema = z.object({
  id: z.string(),
  requestedBy: z.string(),
  reason: z.string(),
  hours: z.number().int(),
  requestedAt: z.string(),
  status: z.enum(["Requested", "Approved", "Rejected", "Revoked"]),
  decidedAt: z.string().nullable(),
  validUntil: z.string().nullable(),
  isActive: z.boolean(),
});

/** An emergency access. */
export type EmergencyAccess = z.infer<typeof emergencyAccessSchema>;

/** Emergency accesses, newest first. */
export const emergencyAccessListSchema = z.array(emergencyAccessSchema);

/** Plan, service state and use of the tenant (`GET /tenant/subscription`, T-113). */
export const subscriptionSchema = z.object({
  status: serviceStatusSchema,
  planCode: z.string(),
  entitlements: z.array(z.string()),
  employeeLimit: z.number().int().nullable(),
  trialEndsOn: localDateSchema.nullable(),
  employeeLimitExceeded: z.boolean(),
  usage: z.array(
    z.object({
      month: localDateSchema,
      activeEmployees: z.number().int(),
      employees: z.number().int(),
    }),
  ),
});

/** Plan, service state and use of the tenant. */
export type Subscription = z.infer<typeof subscriptionSchema>;

/** Payment method reported by the payment provider (T-134, spec 20.6). */
export const paymentMethodKindSchema = z.enum(["SepaDebit", "Card", "BankTransfer"]);

/** The billing profile of the tenant (T-134). */
export const billingProfileSchema = z.object({
  companyName: z.string(),
  legalForm: z.string(),
  street: z.string(),
  postalCode: z.string(),
  city: z.string(),
  countryCode: z.string(),
  vatId: z.string().nullable(),
  vatIdValidatedAt: z.string().nullable(),
  invoiceEmail: z.string(),
  paymentMethodKind: paymentMethodKindSchema.nullable(),
  updatedAt: z.string(),
});

/** The billing profile of the tenant. */
export type BillingProfile = z.infer<typeof billingProfileSchema>;

/** `GET /tenant/billing-profile` (T-134). */
export const billingProfileStateSchema = z.object({
  profile: billingProfileSchema.nullable(),
  isEditable: z.boolean(),
});

/** The stored billing profile, if any, and whether the installation stores one. */
export type BillingProfileState = z.infer<typeof billingProfileStateSchema>;

/** State of the subscription at the payment provider (T-135). */
export const paymentSubscriptionStatusSchema = z.enum([
  "Incomplete",
  "Trialing",
  "Active",
  "PastDue",
  "Unpaid",
  "Paused",
  "Canceled",
]);

/** State of the subscription at the payment provider. */
export type PaymentSubscriptionStatus = z.infer<typeof paymentSubscriptionStatusSchema>;

/** Mirror of the customer and subscription at the payment provider (T-135). */
export const paymentAccountSchema = z.object({
  customerId: z.string(),
  subscriptionId: z.string().nullable(),
  status: paymentSubscriptionStatusSchema.nullable(),
  planCode: z.string().nullable(),
  currentPeriodStart: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  cancelAt: z.string().nullable(),
  paymentMethod: paymentMethodKindSchema.nullable(),
  syncedAt: z.string(),
});

/** Mirror of the customer and subscription at the payment provider. */
export type PaymentAccount = z.infer<typeof paymentAccountSchema>;

/** `GET /tenant/payment` (T-135). */
export const paymentStateSchema = z.object({
  isAvailable: z.boolean(),
  account: paymentAccountSchema.nullable(),
  purchasablePlans: z.array(z.string()),
  /** Current plan contracted on request (Enterprise, F-64); `null` otherwise and from servers before F-64. */
  planOnRequest: z.string().nullable().default(null),
  /** Address of the operator for contracts on request, if configured. */
  contactEmail: z.string().nullable().default(null),
});

/** Payment state of the tenant. */
export type PaymentState = z.infer<typeof paymentStateSchema>;

/** Page of the payment provider the browser is sent to (T-135). */
export const paymentRedirectSchema = z.object({
  url: z.url({ protocol: /^https$/ }),
});

/** Answer of `POST /tenant/subscription:change-plan` (F-96). */
export const planChangeSchema = z.object({
  planCode: z.string(),
  /** End of the paid period the change waits for (downgrade); `null` if it applies now (upgrade). */
  effectiveAt: z.string().nullable(),
});

/** Outcome of a plan change. */
export type PlanChange = z.infer<typeof planChangeSchema>;

/** State of a complete export of the tenant (T-138). */
export const dataExportStatusSchema = z.enum(["Pending", "Completed", "Failed", "Expired"]);

/** A complete export of the tenant (T-138, `GET /tenant/exports`). */
export const dataExportSchema = z.object({
  id: z.uuid(),
  status: dataExportStatusSchema,
  requestedAt: z.iso.datetime({ offset: true }),
  completedAt: z.iso.datetime({ offset: true }).nullable(),
  downloadExpiresAt: z.iso.datetime({ offset: true }).nullable(),
  sha256: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  downloadUrl: z.string().startsWith("/api/v1/tenant/exports/").nullable(),
});

/** A complete export of the tenant. */
export type DataExport = z.infer<typeof dataExportSchema>;

/** Exports of the tenant, newest first. */
export const dataExportListSchema = z.array(dataExportSchema);

/** An invoice or credit note of the operator (T-136). */
export const invoiceSchema = z.object({
  id: z.uuid(),
  kind: z.enum(["Invoice", "CreditNote"]),
  number: z.string(),
  issueDate: z.iso.date(),
  periodStart: z.iso.date(),
  periodEnd: z.iso.date(),
  netCents: z.number().int(),
  taxCents: z.number().int(),
  grossCents: z.number().int(),
  taxTreatment: z.enum(["Domestic19", "ReverseChargeEu"]),
  correctedInvoiceNumber: z.string().nullable(),
  documentHash: z.string(),
});

/** An invoice or credit note of the operator. */
export type Invoice = z.infer<typeof invoiceSchema>;

/** `GET /tenant/invoices` (T-136). */
export const invoiceListSchema = z.object({
  isAvailable: z.boolean(),
  invoices: z.array(invoiceSchema),
});

/** Purpose of a contact of the tenant (T-134, spec 20.3.6). */
export const tenantContactKindSchema = z.enum([
  "Contract",
  "Billing",
  "Security",
  "DataProtection",
]);

/** Purpose of a contact of the tenant. */
export type TenantContactKind = z.infer<typeof tenantContactKindSchema>;

/** A contact of the tenant. */
export const tenantContactSchema = z.object({
  kind: tenantContactKindSchema,
  name: z.string(),
  email: z.string(),
});

/** A contact of the tenant. */
export type TenantContact = z.infer<typeof tenantContactSchema>;

/** `GET /tenant/contacts` (T-134). */
export const tenantContactsSchema = z.object({
  contacts: z.array(tenantContactSchema),
  missing: z.array(tenantContactKindSchema),
  isEditable: z.boolean(),
});

/** The contacts of the tenant and the mandatory purposes without one. */
export type TenantContacts = z.infer<typeof tenantContactsSchema>;

/** Kind of a legal document of the operator (T-130). */
export const legalDocumentKindSchema = z.enum(["Terms", "Dpa", "Sla", "PrivacyNotice"]);

/** Kind of a legal document of the operator. */
export type LegalDocumentKind = z.infer<typeof legalDocumentKindSchema>;

/** A version of a legal document (`GET /legal-documents/current`, T-130). */
export const legalDocumentSchema = z.object({
  id: z.string(),
  kind: legalDocumentKindSchema,
  version: z.string(),
  validFrom: localDateSchema,
  contentHash: z.string().regex(/^[0-9a-f]{64}$/u),
  documentRef: z.string(),
  requiresAcceptance: z.boolean(),
});

/** A version of a legal document. */
export type LegalDocument = z.infer<typeof legalDocumentSchema>;

/** State of one kind of legal document for the tenant (`GET /tenant/legal-documents`, T-130). */
export const legalDocumentStatusSchema = z.object({
  document: legalDocumentSchema,
  decision: z.enum(["Accepted", "Declined"]).nullable(),
  inForceVersion: z.string().nullable(),
  isPending: z.boolean(),
  canDecline: z.boolean(),
});

/** State of one kind of legal document for the tenant. */
export type LegalDocumentStatus = z.infer<typeof legalDocumentStatusSchema>;

/** Branding of the employer (T-108). */
export const brandingSchema = z.object({
  tenantName: z.string(),
  defaultLanguage: z.enum(["de", "en"]).nullable(),
  accentColor: z
    .string()
    .regex(/^#[0-9a-f]{6}$/u)
    .nullable(),
  logoDataUrl: z
    .string()
    .regex(/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/u)
    .nullable(),
});

/** Branding of the employer. */
export type Branding = z.infer<typeof brandingSchema>;

/**
 * Features of a tenant, each off by default: the monitoring-related ones (§ 87 Abs. 1 Nr. 6 BetrVG) and
 * education leave under the laws of the federal states (F-21).
 */
export const tenantFeaturesSchema = z.object({
  locationCaptureEnabled: z.boolean(),
  projectTimeReportsPerPersonEnabled: z.boolean(),
  teamComparisonsEnabled: z.boolean(),
  projectTimeTrackingEnabled: z.boolean(),
  managersMayConfigureProjectRequirement: z.boolean(),
  managersMayCreateProjects: z.boolean(),
  /** Missing from servers before F-21: then off. */
  educationLeaveEnabled: z.boolean().default(false),
  /** Persons confirm their month (T-171); missing from servers before T-171: on, as the server's default. */
  monthConfirmationEnabled: z.boolean().default(true),
});

/** Limit of § 7 (7) sentence 4 AZV the supreme service authority chose (F-105). */
export const azvDeletionModeSchema = z.enum(["AfterSettlementPeriod", "AfterCollectionMonth"]);

/** Limit of § 7 (7) sentence 4 AZV. */
export type AzvDeletionMode = z.infer<typeof azvDeletionModeSchema>;

/**
 * Deletion of the time records of federal civil servants under § 7 (7) AZV (F-105); `null` values keep the defaults
 * (no choice of the authority: the earlier latest date; settlement period: the calendar year).
 */
export const tenantAzvRetentionSchema = z.object({
  mode: azvDeletionModeSchema.nullable(),
  deletionMonths: z.number().int().nullable(),
  settlementPeriodStartMonth: z.number().int().nullable(),
  settlementPeriodMonths: z.number().int().nullable(),
});

/** Deletion of the time records of federal civil servants. */
export type TenantAzvRetention = z.infer<typeof tenantAzvRetentionSchema>;

/** Retention periods in years; `null` keeps the platform default. */
export const tenantRetentionSchema = z.object({
  rawDataYears: z.number().int().nullable(),
  absenceYears: z.number().int().nullable(),
  aggregateYears: z.number().int().nullable(),
  /** Deletion under § 7 (7) AZV (F-105); absent in the platform's retention and from servers before F-105. */
  azv: tenantAzvRetentionSchema.nullable().default(null),
});

/** Retention periods. */
export type TenantRetention = z.infer<typeof tenantRetentionSchema>;

/** Configuration of a tenant (T-108). */
export const tenantConfigurationSchema = z.object({
  features: tenantFeaturesSchema,
  retention: tenantRetentionSchema,
  branding: z.object({
    defaultLanguage: z.enum(["de", "en"]).nullable(),
    accentColor: z.string().nullable(),
  }),
  platformRetention: tenantRetentionSchema,
  /**
   * Flexitime rules of federal civil servants under § 7 (5) AZV (F-117): flexi days per year, `null` for twelve;
   * defaulted for a server before F-117.
   */
  azvFlexitime: z
    .object({ maxFlexiDays: z.number().int().nullable() })
    .default({ maxFlexiDays: null }),
});

/** Configuration of a tenant. */
export type TenantConfiguration = z.infer<typeof tenantConfigurationSchema>;

/** A rule override based on a collective or works agreement (T-108). */
export const ruleOverrideSchema = z.object({
  profileId: z.string(),
  validFrom: z.string(),
  validTo: z.string().nullable(),
  parameters: z.record(z.string(), z.unknown()),
  legalBasis: z.string(),
  legalBasisKind: z.enum(["CollectiveAgreement", "WorksAgreement"]),
});

/** A rule override. */
export type RuleOverride = z.infer<typeof ruleOverrideSchema>;

/** The rule overrides in force and the profiles they can change. */
export const ruleOverridesSchema = z.object({
  overrides: z.array(
    ruleOverrideSchema.extend({
      /** Parameters that would relax the profile and have no effect (T-154). */
      ineffectiveParameters: z.array(z.string()).optional(),
    }),
  ),
  profiles: z.array(
    z.object({
      id: z.string(),
      version: z.string(),
      overridableParameters: z.array(z.string()),
      collectiveAgreementOnlyParameters: z.array(z.string()),
    }),
  ),
});

/** Rule overrides with their profiles. */
export type RuleOverrides = z.infer<typeof ruleOverridesSchema>;

/** Kind of legal basis of a deviation from the ArbZG (T-154). */
export const legalDeviationTypeSchema = z.enum([
  "CollectiveAgreement",
  "WorksAgreement",
  "AuthorityPermit",
  "EmergencyCase",
  "OperationalReasons",
]);

/** Kind of legal basis of a deviation. */
export type LegalDeviationType = z.infer<typeof legalDeviationTypeSchema>;

/** Whom a deviation applies to (T-154). */
export const deviationScopeKindSchema = z.enum(["Tenant", "Establishment", "Employee"]);

/** Whom a deviation applies to. */
export type DeviationScopeKind = z.infer<typeof deviationScopeKindSchema>;

/** A legal deviation with its state (T-154). */
export const legalDeviationSchema = z.object({
  id: z.string(),
  type: legalDeviationTypeSchema,
  normCode: z.string(),
  normVersion: z.string(),
  ruleId: z.string().nullable(),
  legalReference: z.string().nullable(),
  scopeKind: deviationScopeKindSchema,
  scopeTargetId: z.string().nullable(),
  validFrom: z.string(),
  validTo: z.string().nullable(),
  parameters: z.record(z.string(), z.unknown()),
  reference: z.string(),
  authority: z.string().nullable(),
  enablingAgreement: z.string().nullable(),
  justification: z.string().nullable(),
  status: z.enum(["Scheduled", "Active", "Expired", "Revoked", "Ineffective"]),
  createdAt: z.string(),
  createdBy: z.string(),
  revokedAt: z.string().nullable(),
  revokedBy: z.string().nullable(),
  revocationReason: z.string().nullable(),
});

/** A legal deviation. */
export type LegalDeviation = z.infer<typeof legalDeviationSchema>;

/** The limit a norm sets for one parameter (T-154). */
export const deviationParameterBoundSchema = z.object({
  name: z.string(),
  required: z.boolean(),
  min: z.number().nullable(),
  max: z.number().nullable(),
  values: z.array(z.string()).nullable(),
  earliestStart: z.string().nullable(),
  latestStart: z.string().nullable(),
  durationSeconds: z.number().nullable(),
});

/** The limit a norm sets for one parameter. */
export type DeviationParameterBound = z.infer<typeof deviationParameterBoundSchema>;

/** A version of a norm that allows deviations (T-154). */
export const deviationNormSchema = z.object({
  code: z.string(),
  version: z.string(),
  validFrom: z.string(),
  validTo: z.string().nullable(),
  ruleId: z.string(),
  legalReference: z.string(),
  permitLegalReference: z.string().nullable(),
  title: z.string(),
  conditions: z.string(),
  types: z.array(legalDeviationTypeSchema),
  scopes: z.array(deviationScopeKindSchema),
  profiles: z.array(z.string()),
  maxDurationDays: z.number().int().nullable(),
  parameters: z.array(deviationParameterBoundSchema),
});

/** A version of a norm that allows deviations. */
export type DeviationNorm = z.infer<typeof deviationNormSchema>;

/** The deviations of the tenant with the norms and establishments to record new ones (T-154). */
export const legalDeviationsSchema = z.object({
  deviations: z.array(legalDeviationSchema),
  norms: z.array(deviationNormSchema),
  establishments: z.array(z.object({ id: z.string(), name: z.string() })),
  maxEmergencyBackdatingDays: z.number().int(),
});

/** The deviations of the tenant. */
export type LegalDeviations = z.infer<typeof legalDeviationsSchema>;

/** Visibility of a project (T-121). */
export const projectVisibilitySchema = z.enum(["AllEmployees", "Members"]);

/** Life cycle of a project (T-121). */
export const projectStatusSchema = z.enum(["Active", "Archived"]);

/** A project (`GET /projects`, `GET /me/projects`). */
export const projectSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  costCenter: z.string().nullable(),
  customerReference: z.string().nullable(),
  validFrom: localDateSchema.nullable(),
  validTo: localDateSchema.nullable(),
  isBillable: z.boolean(),
  visibility: projectVisibilitySchema,
  status: projectStatusSchema,
  /** The customer (T-184); missing in caches from before T-184. */
  customerId: z.uuid().nullable().optional(),
});

/** Current and upcoming participants of a project in the project list (F-27). */
export const projectParticipantsSchema = z.object({
  teams: z.number().int(),
  employees: z.number().int(),
  responsibles: z.number().int(),
});

/** A project in the project list (`GET /projects`, F-27). */
export const projectListItemSchema = projectSchema.extend({
  participants: projectParticipantsSchema,
});

/** How strongly project attributions are asked for (spec 6.5.5). */
export const projectRequirementModeSchema = z.enum([
  "Disabled",
  "Optional",
  "RequiredByDayClose",
  "PromptAtClockOut",
]);

/** Requirement that applies to the current user. */
export const projectRequirementSchema = z.object({
  mode: projectRequirementModeSchema,
  allocationDeadlineWorkingDays: z.number().int(),
  source: z.string(),
  scopeKind: z.string().nullable(),
  /** Whether project time also needs an activity (T-184); never blocking. */
  requireActivity: z.boolean().optional(),
});

/** An activity the person can choose (T-184). */
export const myActivitySchema = z.object({ id: z.uuid(), code: z.string(), name: z.string() });

/** `GET /me/projects`; the activities are missing in caches from before T-184. */
export const myProjectsSchema = z.object({
  requirement: projectRequirementSchema,
  projects: z.array(projectSchema),
  activities: z.array(myActivitySchema).optional(),
  allowedActivities: z
    .array(z.object({ projectId: z.uuid(), activityIds: z.array(z.uuid()) }))
    .optional(),
  /** Projects with „Budget fast erschöpft“ (T-185, tenant switch); missing in caches before T-185. */
  budgetNearlyExhaustedProjectIds: z.array(z.uuid()).optional(),
  /** Whether the person may create projects (F-62); missing in caches before F-62, read as `false`. */
  mayCreateProjects: z.boolean().optional(),
});

/** One sum of a project report (T-123, T-169); `projectId` is `null` for time without a project. */
export const projectReportRowSchema = z.object({
  projectId: z.uuid().nullable(),
  projectCode: z.string().nullable(),
  projectName: z.string().nullable(),
  teamId: z.uuid().nullable(),
  employeeId: z.uuid().nullable(),
  personnelNumber: z.string().nullable(),
  seconds: z.number().int(),
  date: localDateSchema.nullable(),
  isBillable: z.boolean().nullable(),
  // T-184; defaults keep answers of a server before T-184 readable.
  customerId: z.uuid().nullable().default(null),
  customerNumber: z.string().nullable().default(null),
  customerName: z.string().nullable().default(null),
  activityId: z.uuid().nullable().default(null),
  activityCode: z.string().nullable().default(null),
  activityName: z.string().nullable().default(null),
});

/** The project time of one customer: the sum of its project rows (T-184). */
export const projectReportCustomerSchema = z.object({
  customerId: z.uuid().nullable(),
  customerNumber: z.string().nullable(),
  customerName: z.string().nullable(),
  seconds: z.number().int(),
});

/** Totals of a project report: the sums of its rows. */
export const projectReportTotalsSchema = z.object({
  workSeconds: z.number().int(),
  billableSeconds: z.number().int(),
  nonBillableSeconds: z.number().int(),
  unattributedSeconds: z.number().int(),
});

/** `GET /reports/projects` (T-169): sums never ranked by time (spec 6.5.7). */
export const projectReportSchema = z.object({
  from: localDateSchema,
  to: localDateSchema,
  byTeam: z.boolean(),
  byPerson: z.boolean(),
  byDay: z.boolean(),
  perPersonAvailable: z.boolean(),
  projectId: z.uuid().nullable(),
  rows: z.array(projectReportRowSchema),
  totals: projectReportTotalsSchema,
  byActivity: z.boolean().default(false),
  customerId: z.uuid().nullable().default(null),
  customers: z.array(projectReportCustomerSchema).default([]),
  /** Whether time on days of federal civil servants under § 7 (8) AZV is left out (F-116). */
  azvDaysOmitted: z.boolean().default(false),
});

/** Result per submitted project attribution (T-122). */
export const attributionResultSchema = z.object({
  id: z.uuid(),
  status: z.enum(["Accepted", "Duplicate", "Rejected"]),
  error: errorResponseSchema.nullable(),
  hints: z.array(z.string()),
  followUp: z.enum(["None", "AttributionRequest"]),
});

/** `POST /project-attributions:batch`. */
export const attributionBatchResponseSchema = z.object({
  results: z.array(attributionResultSchema),
});

/** A block of a working day with its project (T-122). */
export const attributedBlockSchema = z.object({
  kind: z.enum(["Work", "Break"]),
  start: instantSchema,
  end: instantSchema,
  projectId: z.uuid().nullable(),
  isOpen: z.boolean(),
  notice: z.enum(["None", "ProjectArchived"]),
  /** Activity of the deciding attribution (T-184). */
  activityId: z.uuid().nullable().optional(),
  /** Whether that activity was accepted offline although not allowed (PINV-P08). */
  activityNotAllowed: z.boolean().optional(),
});

/** The project view of a working day (T-122). */
export const projectDaySchema = z.object({
  date: localDateSchema,
  isClosed: z.boolean(),
  workSeconds: z.number().int(),
  unattributedWorkSeconds: z.number().int(),
  isAttributionOpen: z.boolean(),
  requirement: projectRequirementModeSchema,
  allocationDeadlineWorkingDays: z.number().int(),
  projects: z.array(z.object({ projectId: z.uuid(), seconds: z.number().int() })),
  blocks: z.array(attributedBlockSchema),
  /** Project time without an activity (T-184). */
  withoutActivitySeconds: z.number().int().optional(),
  /** Whether the requirement also asks for an activity on the day (T-184). */
  requireActivity: z.boolean().optional(),
  /** Whether the person may choose a project on the day (F-61); missing from servers before F-61. */
  hasSelectableProjects: z.boolean().optional(),
});

/** `GET /me/project-days`. */
export const projectDaysSchema = z.object({
  isAvailable: z.boolean(),
  days: z.array(projectDaySchema),
});

/** Kind of a project member (spec 6.5.6). */
export const projectMemberKindSchema = z.enum(["Team", "Employee"]);

/** A member of a project. */
export const projectMemberSchema = z.object({
  memberKind: projectMemberKindSchema,
  memberId: z.uuid(),
  from: localDateSchema,
  to: localDateSchema.nullable(),
});

/** A responsibility for a project. */
export const projectResponsibleSchema = z.object({
  employeeId: z.uuid(),
  from: localDateSchema,
  to: localDateSchema.nullable(),
});

/** `GET /projects/{id}` and the answers of project changes. */
export const projectDetailSchema = z.object({
  project: projectSchema,
  members: z.array(projectMemberSchema),
  responsibles: z.array(projectResponsibleSchema),
  /** The activities the project allows (T-184); empty for every active activity. */
  allowedActivityIds: z.array(z.uuid()).default([]),
});

/** Level of a requirement policy. */
export const policyScopeKindSchema = z.enum(["Tenant", "Establishment", "Team", "Employment"]);

/** A requirement policy (`GET /project-requirement-policies`). */
export const projectRequirementPolicySchema = z.object({
  id: z.uuid(),
  scopeKind: policyScopeKindSchema,
  scopeId: z.uuid(),
  mode: projectRequirementModeSchema,
  allocationDeadlineWorkingDays: z.number().int(),
  changedByEmployeeId: z.uuid(),
  changedAt: instantSchema,
  requireActivity: z.boolean().default(false),
});

/** `DELETE /project-requirement-policies/{id}`. */
export const projectRequirementPolicyRemovalSchema = z.object({ id: z.uuid() });

/** A team definition (`GET /teams`). */
export const teamDefinitionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  leadEmployeeId: z.uuid(),
  establishmentId: z.uuid().nullable(),
  validFrom: localDateSchema,
  validTo: localDateSchema.nullable(),
});

/** A project. */
export type Project = z.infer<typeof projectSchema>;
/** A project in the project list with its participant counts. */
export type ProjectListItem = z.infer<typeof projectListItemSchema>;
/** Project member kind. */
export type ProjectMemberKind = z.infer<typeof projectMemberKindSchema>;
/** A project member. */
export type ProjectMember = z.infer<typeof projectMemberSchema>;
/** A project with members and responsibilities. */
export type ProjectDetail = z.infer<typeof projectDetailSchema>;
/** Level of a requirement policy. */
export type PolicyScopeKind = z.infer<typeof policyScopeKindSchema>;
/** A requirement policy. */
export type ProjectRequirementPolicy = z.infer<typeof projectRequirementPolicySchema>;
/** A team definition. */
export type TeamDefinition = z.infer<typeof teamDefinitionSchema>;
/** Requirement mode. */
export type ProjectRequirementMode = z.infer<typeof projectRequirementModeSchema>;
/** Projects and requirement of the current user. */
export type MyProjects = z.infer<typeof myProjectsSchema>;
/** One sum of a project report. */
export type ProjectReportRow = z.infer<typeof projectReportRowSchema>;
/** Totals of a project report. */
export type ProjectReportTotals = z.infer<typeof projectReportTotalsSchema>;
/** A project report. */
export type ProjectReport = z.infer<typeof projectReportSchema>;
/** The project time of one customer. */
export type ProjectReportCustomer = z.infer<typeof projectReportCustomerSchema>;
/** An activity the person can choose. */
export type MyActivity = z.infer<typeof myActivitySchema>;
/** Result of a submitted attribution. */
export type AttributionResult = z.infer<typeof attributionResultSchema>;
/** Batch response of attributions. */
export type AttributionBatchResponse = z.infer<typeof attributionBatchResponseSchema>;
/** The project view of a working day. */
export type ProjectDay = z.infer<typeof projectDaySchema>;

/** One attribution of `POST /project-attributions:batch` (request body). */
export interface AttributionRequestBody {
  readonly id: string;
  readonly employeeId: string | null;
  readonly kind: "Switch" | "Range";
  readonly projectId: string | null;
  readonly occurredAt: string | null;
  readonly rangeStart: string | null;
  readonly rangeEnd: string | null;
  readonly supersedesAttributionId: string | null;
  readonly note: string | null;
  readonly source: EventSource;
  readonly recordedOffline: boolean;
  /** The activity of the project time (T-184); only with a project. */
  readonly activityId?: string | null;
}

/** A locked project period (`GET /project-period-locks`, T-122, `PINV-P05`). */
export const projectPeriodLockSchema = z.object({
  id: z.uuid(),
  /** The project, or `null` for every project. */
  projectId: z.uuid().nullable(),
  from: localDateSchema,
  to: localDateSchema,
  reason: z.string(),
  lockedAt: instantSchema,
  releasedAt: instantSchema.nullable(),
});

/** State of a request for an attribution in a locked period. */
export const attributionRequestStatusSchema = z.enum(["Pending", "Approved", "Rejected"]);

/** A request for an attribution in a locked period (answer of create, approve and reject). */
export const projectAttributionRequestSchema = z.object({
  id: z.uuid(),
  employeeId: z.uuid(),
  attributionId: z.uuid(),
  kind: z.enum(["Switch", "Range"]),
  projectId: z.uuid().nullable(),
  occurredAt: instantSchema.nullable(),
  rangeStart: instantSchema.nullable(),
  rangeEnd: instantSchema.nullable(),
  reason: z.string(),
  status: attributionRequestStatusSchema,
  requestedAt: instantSchema,
  decidedAt: instantSchema.nullable(),
  decisionNote: z.string().nullable(),
});

/** A request as listed by `GET /project-attribution-requests` (F-07). */
export const projectAttributionRequestItemSchema = projectAttributionRequestSchema.extend({
  employeeName: z.string().nullable(),
  projectCode: z.string().nullable(),
  projectName: z.string().nullable(),
  /** Whether the current user may decide it now (never for own requests). */
  mayDecide: z.boolean(),
});

/** Working days with projects (`GET /me/project-days`). */
export type ProjectDays = z.infer<typeof projectDaysSchema>;
/** A locked project period. */
export type ProjectPeriodLock = z.infer<typeof projectPeriodLockSchema>;
/** State of an attribution request. */
export type AttributionRequestStatus = z.infer<typeof attributionRequestStatusSchema>;
/** A request for an attribution in a locked period. */
export type ProjectAttributionRequest = z.infer<typeof projectAttributionRequestSchema>;
/** A listed request with names and the decision right of the current user. */
export type ProjectAttributionRequestItem = z.infer<typeof projectAttributionRequestItemSchema>;

/** Bot protection challenge of the registration (`GET /signup/challenge`, T-132). */
export const signupChallengeSchema = z.object({
  challenge: z.string(),
  difficultyBits: z.number().int().min(1).max(32),
});

/** Bot protection challenge of the registration. */
export type SignupChallenge = z.infer<typeof signupChallengeSchema>;

/** Answer to a registration (`POST /signup`, T-132): the same for new and registered companies. */
export const signupAcceptedSchema = z.object({ next: z.literal("CheckEmail") });

/** Result of a confirmed registration (`POST /signup/verify`, T-132). */
export const signupVerifiedSchema = z.object({ tenantId: z.string() });

/** A step of the setup checklist (T-133, spec 20.3.1). */
export const setupStepSchema = z.enum([
  "Establishment",
  "WorkTimeModels",
  "Employees",
  "Invitations",
  "WorksCouncil",
  "EmployeeInformation",
  "FirstClockEvent",
  "LeavePolicy",
]);

/** `GET /setup/checklist`. */
export const setupChecklistSchema = z.array(
  z.object({
    step: setupStepSchema,
    isConfirmable: z.boolean(),
    isDone: z.boolean(),
    confirmedAt: instantSchema.nullable(),
  }),
);

/** `GET /setup/work-time-model-templates`. */
export const workTimeModelTemplatesSchema = z.array(
  z.object({
    key: z.string(),
    name: z.string(),
    kind: workTimeModelKindSchema,
    weeklyTargetSeconds: z.number().int(),
    workingDays: z.array(dayOfWeekSchema),
    coreTimeStart: z.string().nullable(),
    coreTimeEnd: z.string().nullable(),
    flexitimeMaxPositiveSeconds: z.number().int().nullable(),
    flexitimeMaxNegativeSeconds: z.number().int().nullable(),
  }),
);

/** An error in a line of an employee import. */
export const importRowErrorSchema = z.object({
  line: z.number().int(),
  field: z.string(),
  code: z.string(),
  message: z.string(),
});

/** `POST /setup/employees:import` and `POST /setup/employees`. */
export const employeeImportSchema = z.object({
  checked: z.number().int(),
  created: z.number().int(),
  invitations: z.number().int(),
  errors: z.array(importRowErrorSchema),
});

/** Status of an invitation. */
export const invitationStatusSchema = z.enum(["Pending", "Sent", "Failed"]);

/** `GET /setup/invitations`. */
export const invitationsSchema = z.array(
  z.object({
    employeeId: z.uuid(),
    personnelNumber: z.string(),
    displayName: z.string(),
    status: invitationStatusSchema,
    attempts: z.number().int(),
    lastErrorCode: z.string().nullable(),
    createdAt: instantSchema,
    sentAt: instantSchema.nullable(),
  }),
);

/** `POST /setup/invitations/{employeeId}:resend`. */
export const invitationStatusResponseSchema = z.object({
  employeeId: z.uuid(),
  status: invitationStatusSchema,
  attempts: z.number().int(),
  lastErrorCode: z.string().nullable(),
});

/** `POST /setup/invitations:send`. */
export const invitationDispatchSchema = z.object({
  sent: z.number().int(),
  failed: z.number().int(),
});

/** A step of the setup checklist. */
export type SetupStep = z.infer<typeof setupStepSchema>;
/** The setup checklist. */
export type SetupChecklist = z.infer<typeof setupChecklistSchema>;
/** Work time model templates. */
export type WorkTimeModelTemplates = z.infer<typeof workTimeModelTemplatesSchema>;
/** Outcome of adding employees. */
export type EmployeeImport = z.infer<typeof employeeImportSchema>;
/** Invitations. */
export type Invitations = z.infer<typeof invitationsSchema>;
/** Status of an invitation. */
export type InvitationStatus = z.infer<typeof invitationStatusSchema>;

/** Support status of a special regime in the scope register (T-157, ADR-0097). */
export const regimeSupportStatusSchema = z.enum(["Supported", "PartiallySupported", "OutOfScope"]);

/** Support status of a special regime. */
export type RegimeSupportStatus = z.infer<typeof regimeSupportStatusSchema>;

/** An official legal source with its check date. */
const scopeLegalSourceSchema = z.object({
  citation: z.string(),
  url: z.url(),
  checkedOn: localDateSchema,
  primarySourceVerified: z.boolean(),
});

/** `GET /special-regimes`: the scope register of special industries and regimes. */
export const scopeRegisterSchema = z.object({
  version: z.string(),
  checkedOn: localDateSchema,
  regimes: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      scope: z.string(),
      status: regimeSupportStatusSchema,
      summary: z.string(),
      checkedRuleIds: z.array(z.string()),
      uncheckedRules: z.array(z.object({ reference: z.string(), description: z.string() })),
      legalSources: z.array(scopeLegalSourceSchema),
      checkedOn: localDateSchema,
    }),
  ),
  pendingLegislation: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      stage: z.string(),
      version: z.string(),
      checkedOn: localDateSchema,
      sources: z.array(scopeLegalSourceSchema),
    }),
  ),
});

/** The scope register. */
export type ScopeRegister = z.infer<typeof scopeRegisterSchema>;

/** A special regime of the scope register. */
export type SpecialRegime = ScopeRegister["regimes"][number];

/** `GET` and `PUT /tenants/{id}/special-regimes`: the special regimes selected by the tenant. */
export const specialRegimeSelectionsSchema = z.object({
  registerVersion: z.string(),
  selections: z.array(
    z.object({
      regimeId: z.string(),
      currentStatus: regimeSupportStatusSchema,
      acknowledgedStatus: regimeSupportStatusSchema,
      registerVersion: z.string(),
      acknowledgedAt: instantSchema,
      needsReconfirmation: z.boolean(),
    }),
  ),
});

/** The special regimes selected by the tenant. */
export type SpecialRegimeSelections = z.infer<typeof specialRegimeSelectionsSchema>;

/** Kind of a defect in the audit chain (F-01, ADR-0015); unknown future kinds stay readable. */
export const auditChainBreakKindSchema = z.string();

/** `GET /audit/verify`: verification of the tenant's audit hash chain (F-01, ADR-0100). */
export const auditVerificationSchema = z.object({
  isIntact: z.boolean(),
  verifiedAt: instantSchema,
  entriesChecked: z.number().int().nonnegative(),
  lastSequence: z.number().int().nonnegative(),
  breakCount: z.number().int().nonnegative(),
  firstBreakSequence: z.number().int().nullable(),
  breaks: z.array(z.object({ sequence: z.number().int(), kind: auditChainBreakKindSchema })),
  anchorsChecked: z.number().int().nonnegative(),
  latestAnchor: z
    .object({
      day: localDateSchema,
      sequence: z.number().int(),
      entryHash: z.string().regex(/^[0-9a-f]{64}$/),
    })
    .nullable(),
});

/** Result of a verification of the audit chain. */
export type AuditVerification = z.infer<typeof auditVerificationSchema>;

/** `POST /client-logs` (F-68): the number of reports written to the log. */
export const clientLogReceiptSchema = z.object({
  accepted: z.number().int(),
});
